import { useEffect, useRef, useState } from 'react';
import './AdminDialogos.css';

const NIVEIS = ['beginner', 'intermediate', 'advanced'];
const NIVEL_LABELS = { beginner: 'Iniciante', intermediate: 'Intermediário', advanced: 'Avançado' };

const EN_VOICES = [
  { name: 'en-US-Wavenet-A', label: 'US-A (Feminina)' },
  { name: 'en-US-Wavenet-B', label: 'US-B (Masculina)' },
  { name: 'en-US-Wavenet-C', label: 'US-C (Feminina)' },
  { name: 'en-US-Wavenet-D', label: 'US-D (Masculina)' },
  { name: 'en-US-Wavenet-F', label: 'US-F (Feminina)' },
  { name: 'en-GB-Wavenet-A', label: 'GB-A (Feminina)' },
  { name: 'en-GB-Wavenet-B', label: 'GB-B (Masculina)' },
  { name: 'en-GB-Wavenet-C', label: 'GB-C (Feminina)' },
  { name: 'en-GB-Wavenet-D', label: 'GB-D (Masculina)' },
  { name: 'en-GB-Wavenet-F', label: 'GB-F (Feminina)' },
];

const PAR_PRESETS = {
  hm: { voz_a: 'en-US-Wavenet-D', voz_b: 'en-US-Wavenet-C', nome_a: 'James', nome_b: 'Sarah' },
  mm: { voz_a: 'en-US-Wavenet-B', voz_b: 'en-US-Wavenet-D', nome_a: 'Tom',   nome_b: 'David' },
  ff: { voz_a: 'en-US-Wavenet-A', voz_b: 'en-US-Wavenet-F', nome_a: 'Emma',  nome_b: 'Lisa'  },
};

const PAR_LABELS = { hm: 'Homem & Mulher', mm: 'Homem & Homem', ff: 'Mulher & Mulher' };

const EMPTY_FORM = {
  titulo: '', topico: '', nivel: 'intermediate', par_tipo: 'hm',
  voz_a: 'en-US-Wavenet-D', voz_b: 'en-US-Wavenet-C',
  nome_a: 'James', nome_b: 'Sarah', ativo: true,
};

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function AdminDialogos() {
  const [dialogos,    setDialogos]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [formOpen,    setFormOpen]    = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [linhas,      setLinhas]      = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [confirmId,   setConfirmId]   = useState(null);
  const [toast,       setToast]       = useState(null);
  const [search,      setSearch]      = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [aiModal,     setAiModal]     = useState(false);
  const [aiForm,      setAiForm]      = useState({ topico: '', nivel: 'intermediate', numTrocas: 12 });
  const [aiLoading,   setAiLoading]   = useState(false);
  const toastTimer = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }

  async function loadDialogos() {
    setLoading(true);
    try {
      const data = await apiFetch('/api/dialogos/all');
      setDialogos(data);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDialogos(); }, []); // eslint-disable-line

  function openCreate() {
    setForm(EMPTY_FORM);
    setLinhas([]);
    setEditingId(null);
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function openEdit(id) {
    try {
      const data = await apiFetch(`/api/dialogos/${id}`);
      setForm({
        titulo:   data.titulo   || '',
        topico:   data.topico   || '',
        nivel:    data.nivel    || 'intermediate',
        par_tipo: data.par_tipo || 'hm',
        voz_a:    data.voz_a    || 'en-US-Wavenet-D',
        voz_b:    data.voz_b    || 'en-US-Wavenet-C',
        nome_a:   data.nome_a   || 'James',
        nome_b:   data.nome_b   || 'Sarah',
        ativo:    data.ativo === 1 || data.ativo === true,
      });
      setLinhas((data.linhas || []).map(l => ({
        speaker: l.speaker, texto_en: l.texto_en, texto_pt: l.texto_pt || ''
      })));
      setEditingId(id);
      setFormOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setLinhas([]);
  }

  function handleParTipo(par_tipo) {
    const preset = PAR_PRESETS[par_tipo];
    setForm(f => ({ ...f, par_tipo, ...preset }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titulo.trim()) { showToast('Título obrigatório.', 'error'); return; }
    setSaving(true);
    try {
      const body = { ...form, linhas };
      if (editingId) {
        await apiFetch(`/api/dialogos/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        showToast('Diálogo atualizado!');
      } else {
        await apiFetch('/api/dialogos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        showToast('Diálogo criado!');
      }
      closeForm();
      loadDialogos();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await apiFetch(`/api/dialogos/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      showToast('Diálogo excluído.');
      loadDialogos();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  async function toggleAtivo(d) {
    try {
      const full = await apiFetch(`/api/dialogos/${d.id}`);
      await apiFetch(`/api/dialogos/${d.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...full, ativo: !d.ativo, linhas: full.linhas }),
      });
      showToast(d.ativo ? 'Diálogo desativado.' : 'Diálogo ativado!');
      loadDialogos();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  // ── Line editor helpers ──────────────────────────────────
  function addLinha() {
    const lastSpeaker = linhas.length > 0 ? linhas[linhas.length - 1].speaker : 'B';
    setLinhas(prev => [...prev, { speaker: lastSpeaker === 'A' ? 'B' : 'A', texto_en: '', texto_pt: '' }]);
  }

  function updateLinha(idx, field, value) {
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  function removeLinha(idx) {
    setLinhas(prev => prev.filter((_, i) => i !== idx));
  }

  function moveLinha(idx, dir) {
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= linhas.length) return;
    const next = [...linhas];
    [next[idx], next[target]] = [next[target], next[idx]];
    setLinhas(next);
  }

  // ── AI generation ────────────────────────────────────────
  async function gerarComIA() {
    if (!aiForm.topico.trim()) { showToast('Informe o tópico.', 'error'); return; }
    setAiLoading(true);
    try {
      const res = await fetch('/api/dialogos/gerar-ia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...aiForm, nomeA: form.nome_a, nomeB: form.nome_b }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLinhas(data.linhas.map(l => ({ speaker: l.speaker, texto_en: l.texto_en, texto_pt: l.texto_pt || '' })));
      setAiModal(false);
      showToast(`${data.linhas.length} linhas geradas pela IA!`);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setAiLoading(false);
    }
  }

  // ── Filter ───────────────────────────────────────────────
  const filtered = dialogos.filter(d => {
    const matchSearch = !search || d.titulo.toLowerCase().includes(search.toLowerCase()) || (d.topico || '').toLowerCase().includes(search.toLowerCase());
    const matchNivel  = !filterNivel || d.nivel === filterNivel;
    return matchSearch && matchNivel;
  });

  const stats = {
    total:  dialogos.length,
    ativos: dialogos.filter(d => d.ativo).length,
    beg:    dialogos.filter(d => d.nivel === 'beginner').length,
    int:    dialogos.filter(d => d.nivel === 'intermediate').length,
    adv:    dialogos.filter(d => d.nivel === 'advanced').length,
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="adg-page">

      <div className="adg-header">
        <h1><i className="fas fa-comments" /> Gerenciar Diálogos</h1>
        <button className="adg-btn adg-btn-primary" onClick={openCreate}>
          <i className="fas fa-plus" /> Novo Diálogo
        </button>
      </div>

      <div className="adg-stats">
        <span className="adg-stat"><i className="fas fa-list" /> {stats.total} diálogos</span>
        <span className="adg-stat"><i className="fas fa-check-circle" /> {stats.ativos} ativos</span>
        <span className="adg-stat"><i className="fas fa-seedling" /> {stats.beg} Iniciante</span>
        <span className="adg-stat"><i className="fas fa-chart-line" /> {stats.int} Intermediário</span>
        <span className="adg-stat"><i className="fas fa-fire" /> {stats.adv} Avançado</span>
      </div>

      {/* ── Form Panel ── */}
      {formOpen && (
        <div className="adg-form-panel">
          <div className="adg-form-title">
            <i className={editingId ? 'fas fa-edit' : 'fas fa-plus-circle'} />
            {editingId ? 'Editar Diálogo' : 'Novo Diálogo'}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Metadata */}
            <div className="adg-section-label"><i className="fas fa-info-circle" /> Informações</div>

            <div className="adg-field" style={{ marginBottom: 12 }}>
              <label>Título <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                placeholder="Ex: At the Coffee Shop"
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                required
              />
            </div>

            <div className="adg-grid2">
              <div className="adg-field">
                <label>Tópico</label>
                <input
                  type="text"
                  placeholder="Ex: Daily Routine"
                  value={form.topico}
                  onChange={e => setForm(f => ({ ...f, topico: e.target.value }))}
                />
              </div>
              <div className="adg-field">
                <label>Nível</label>
                <select value={form.nivel} onChange={e => setForm(f => ({ ...f, nivel: e.target.value }))}>
                  {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABELS[n]}</option>)}
                </select>
              </div>
            </div>

            <div className="adg-field" style={{ marginBottom: 12 }}>
              <label>Par de Falantes</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.entries(PAR_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleParTipo(key)}
                    style={{
                      flex: 1,
                      padding: '8px 6px',
                      borderRadius: 8,
                      border: `2px solid ${form.par_tipo === key ? '#1e90ff' : '#e2e8f0'}`,
                      background: form.par_tipo === key ? '#eff6ff' : '#f8fafc',
                      color: form.par_tipo === key ? '#1e40af' : '#475569',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      transition: 'all 0.2s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="adg-grid4">
              <div className="adg-field">
                <label>Nome Falante A</label>
                <input value={form.nome_a} onChange={e => setForm(f => ({ ...f, nome_a: e.target.value }))} />
              </div>
              <div className="adg-field">
                <label>Voz A</label>
                <select value={form.voz_a} onChange={e => setForm(f => ({ ...f, voz_a: e.target.value }))}>
                  {EN_VOICES.map(v => <option key={v.name} value={v.name}>{v.label}</option>)}
                </select>
              </div>
              <div className="adg-field">
                <label>Nome Falante B</label>
                <input value={form.nome_b} onChange={e => setForm(f => ({ ...f, nome_b: e.target.value }))} />
              </div>
              <div className="adg-field">
                <label>Voz B</label>
                <select value={form.voz_b} onChange={e => setForm(f => ({ ...f, voz_b: e.target.value }))}>
                  {EN_VOICES.map(v => <option key={v.name} value={v.name}>{v.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label className="adg-toggle-label">
                <input
                  type="checkbox"
                  checked={form.ativo}
                  onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                />
                {form.ativo ? 'Ativo (visível para alunos)' : 'Inativo'}
              </label>
            </div>

            {/* Lines section */}
            <div className="adg-section-label" style={{ marginTop: 22 }}>
              <i className="fas fa-list-ul" /> Linhas do Diálogo
              <button
                type="button"
                className="adg-btn adg-btn-ai"
                style={{ marginLeft: 'auto', padding: '5px 14px', fontSize: '0.75rem' }}
                onClick={() => { setAiForm(f => ({ ...f, topico: form.topico || '' })); setAiModal(true); }}
              >
                <i className="fas fa-robot" /> Gerar com IA
              </button>
            </div>

            <div className="adg-lines-count">
              {linhas.length} {linhas.length === 1 ? 'linha' : 'linhas'} —
              Falante A = {form.nome_a} &nbsp;|&nbsp; Falante B = {form.nome_b}
            </div>

            <div className="adg-lines-editor">
              {linhas.map((linha, idx) => (
                <div key={idx} className="adg-line-row">
                  <div className="adg-line-num">{idx + 1}</div>
                  <button
                    type="button"
                    className={`adg-speaker-btn adg-speaker-${linha.speaker.toLowerCase()}`}
                    onClick={() => updateLinha(idx, 'speaker', linha.speaker === 'A' ? 'B' : 'A')}
                    title="Clique para trocar falante"
                  >
                    {linha.speaker}
                  </button>
                  <div className="adg-line-texts">
                    <textarea
                      rows={2}
                      placeholder="Texto em inglês..."
                      value={linha.texto_en}
                      onChange={e => updateLinha(idx, 'texto_en', e.target.value)}
                    />
                    <textarea
                      rows={2}
                      placeholder="Tradução em português..."
                      value={linha.texto_pt}
                      onChange={e => updateLinha(idx, 'texto_pt', e.target.value)}
                    />
                  </div>
                  <div className="adg-line-actions">
                    <button type="button" onClick={() => moveLinha(idx, 'up')} disabled={idx === 0} title="Mover para cima">↑</button>
                    <button type="button" onClick={() => moveLinha(idx, 'down')} disabled={idx === linhas.length - 1} title="Mover para baixo">↓</button>
                    <button type="button" className="adg-remove-btn" onClick={() => removeLinha(idx)} title="Remover linha">×</button>
                  </div>
                </div>
              ))}
              <button type="button" className="adg-add-linha-btn" onClick={addLinha}>
                <i className="fas fa-plus" /> Adicionar Linha
              </button>
            </div>

            <div className="adg-form-actions">
              <button type="button" className="adg-btn adg-btn-secondary" onClick={closeForm}>
                Cancelar
              </button>
              <button type="submit" className="adg-btn adg-btn-primary" disabled={saving}>
                <i className={`fas fa-${saving ? 'spinner fa-spin' : 'check'}`} />
                {saving ? 'Salvando...' : 'Salvar Diálogo'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="adg-filter">
        <input
          placeholder="Buscar por título ou tópico..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
          <option value="">Todos os níveis</option>
          {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABELS[n]}</option>)}
        </select>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
          {filtered.length}/{dialogos.length}
        </span>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div className="adg-empty"><i className="fas fa-spinner fa-spin" /><p>Carregando...</p></div>
      ) : filtered.length === 0 ? (
        <div className="adg-empty"><i className="fas fa-inbox" /><p>Nenhum diálogo encontrado.</p></div>
      ) : (
        filtered.map(d => (
          <div key={d.id} className={`adg-card${!d.ativo ? ' inactive' : ''}`}>
            <div className="adg-card-info">
              <div className="adg-card-title">{d.titulo}</div>
              <div className="adg-card-meta">
                <span className={`dg-level ${d.nivel}`} style={{
                  fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  background: d.nivel === 'beginner' ? 'rgba(46,204,113,0.15)' : d.nivel === 'intermediate' ? 'rgba(30,144,255,0.15)' : 'rgba(239,68,68,0.15)',
                  color: d.nivel === 'beginner' ? '#16a34a' : d.nivel === 'intermediate' ? '#1e40af' : '#b91c1c',
                }}>
                  {NIVEL_LABELS[d.nivel]}
                </span>
                <span>{PAR_LABELS[d.par_tipo]}</span>
                {d.topico && <span style={{ color: '#94a3b8' }}>{d.topico}</span>}
                <span>{d.nome_a} &amp; {d.nome_b}</span>
                <span style={{ color: '#94a3b8' }}><i className="fas fa-calendar" style={{ marginRight: 3 }} />{formatDate(d.created_at)}</span>
                {!d.ativo && <span style={{ color: '#ef4444', fontWeight: 700 }}><i className="fas fa-eye-slash" /> Inativo</span>}
              </div>
            </div>
            <div className="adg-card-actions">
              <button
                className={`adg-btn ${d.ativo ? 'adg-btn-secondary' : 'adg-btn-success'}`}
                onClick={() => toggleAtivo(d)}
                title={d.ativo ? 'Desativar' : 'Ativar'}
                style={{ padding: '7px 10px' }}
              >
                <i className={`fas fa-${d.ativo ? 'eye-slash' : 'eye'}`} />
              </button>
              <button className="adg-btn adg-btn-warning" onClick={() => openEdit(d.id)}>
                <i className="fas fa-edit" /> Editar
              </button>
              <button className="adg-btn adg-btn-danger" onClick={() => setConfirmId(d.id)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        ))
      )}

      {/* ── Confirm modal ── */}
      {confirmId && (
        <div className="adg-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setConfirmId(null); }}>
          <div className="adg-modal">
            <h3>Excluir diálogo?</h3>
            <p>Esta ação não pode ser desfeita. Todas as linhas também serão removidas.</p>
            <div className="adg-modal-actions">
              <button className="adg-btn adg-btn-secondary" onClick={() => setConfirmId(null)}>Cancelar</button>
              <button className="adg-btn adg-btn-danger" onClick={() => handleDelete(confirmId)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Generation modal ── */}
      {aiModal && (
        <div className="adg-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setAiModal(false); }}>
          <div className="adg-modal">
            <h3><i className="fas fa-robot" style={{ color: '#7c3aed', marginRight: 8 }} /> Gerar Diálogo com IA</h3>
            <p>A IA criará as linhas de diálogo automaticamente. Você pode revisar e editar antes de salvar.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="adg-field">
                <label>Tópico da conversa <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Ex: Ordering food at a restaurant"
                  value={aiForm.topico}
                  onChange={e => setAiForm(f => ({ ...f, topico: e.target.value }))}
                />
              </div>
              <div className="adg-grid2">
                <div className="adg-field">
                  <label>Nível</label>
                  <select value={aiForm.nivel} onChange={e => setAiForm(f => ({ ...f, nivel: e.target.value }))}>
                    {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABELS[n]}</option>)}
                  </select>
                </div>
                <div className="adg-field">
                  <label>Nº de trocas</label>
                  <input
                    type="number"
                    min={6}
                    max={20}
                    value={aiForm.numTrocas}
                    onChange={e => setAiForm(f => ({ ...f, numTrocas: Number(e.target.value) }))}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: '0.77rem', color: '#15803d', border: '1px solid #bbf7d0' }}>
              Falante A: <strong>{form.nome_a}</strong> &nbsp;|&nbsp; Falante B: <strong>{form.nome_b}</strong>
              <br />Altere os nomes no formulário principal se necessário.
            </div>

            <div className="adg-modal-actions">
              <button className="adg-btn adg-btn-secondary" onClick={() => setAiModal(false)} disabled={aiLoading}>
                Cancelar
              </button>
              <button className="adg-btn adg-btn-ai" onClick={gerarComIA} disabled={aiLoading}>
                <i className={`fas fa-${aiLoading ? 'spinner fa-spin' : 'robot'}`} />
                {aiLoading ? 'Gerando...' : 'Gerar Diálogo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`adg-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
